
import { useCallback, useEffect, useRef } from 'react'
import { enablePatches, produceWithPatches, applyPatches, type Patch } from 'immer'
import type { Node, Edge } from '@xyflow/react'
import { sortParentsFirst } from '@/lib/canvas/sort-parents-first'

enablePatches()

const MAX_HISTORY = 50
const DEBOUNCE_MS = 300

interface PatchEntry {
  nodesPatches: Patch[]
  edgesPatches: Patch[]
  nodesInverse: Patch[]
  edgesInverse: Patch[]
}

interface UseImmerUndoRedoParams {
  nodes: Node[]
  edges: Edge[]
  rawSetNodes: React.Dispatch<React.SetStateAction<Node[]>>
  rawSetEdges: React.Dispatch<React.SetStateAction<Edge[]>>
}

export function useImmerUndoRedo({ nodes, edges, rawSetNodes, rawSetEdges }: UseImmerUndoRedoParams) {
  const historyRef = useRef<PatchEntry[]>([])
  const currentIndexRef = useRef(-1)
  const isUndoRedoRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingPatchesRef = useRef<PatchEntry | null>(null)

  // Clear pending debounce on unmount
  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [])

  const flushPatches = useCallback(() => {
    const pending = pendingPatchesRef.current
    if (!pending) return
    if (pending.nodesPatches.length === 0 && pending.edgesPatches.length === 0) {
      pendingPatchesRef.current = null
      return
    }

    // Truncate forward history (discard redo stack)
    const newHistory = historyRef.current.slice(0, currentIndexRef.current + 1)
    newHistory.push({ ...pending })

    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift()
    } else {
      currentIndexRef.current++
    }

    historyRef.current = newHistory
    pendingPatchesRef.current = null
  }, [])

  const setNodes: React.Dispatch<React.SetStateAction<Node[]>> = useCallback(
    (action) => {
      rawSetNodes((prev) => {
        const updater = typeof action === 'function' ? action : () => action

        if (isUndoRedoRef.current) {
          return updater(prev)
        }

        // Pass real prev (not draft) to updater to avoid leaking Immer proxies.
        // Immer diffs prev vs returned value to compute patches.
        const nextValue = updater(prev)
        const [nextState, patches, inversePatches] = produceWithPatches(prev, () => nextValue)

        if (patches.length > 0) {
          if (!pendingPatchesRef.current) {
            pendingPatchesRef.current = {
              nodesPatches: [], edgesPatches: [],
              nodesInverse: [], edgesInverse: [],
            }
          }
          pendingPatchesRef.current.nodesPatches.push(...patches)
          pendingPatchesRef.current.nodesInverse.unshift(...inversePatches)

          if (debounceRef.current) clearTimeout(debounceRef.current)
          debounceRef.current = setTimeout(flushPatches, DEBOUNCE_MS)
        }

        return nextState
      })
    },
    [rawSetNodes, flushPatches],
  )

  const setEdges: React.Dispatch<React.SetStateAction<Edge[]>> = useCallback(
    (action) => {
      rawSetEdges((prev) => {
        const updater = typeof action === 'function' ? action : () => action

        if (isUndoRedoRef.current) {
          return updater(prev)
        }

        const nextValue = updater(prev)
        const [nextState, patches, inversePatches] = produceWithPatches(prev, () => nextValue)

        if (patches.length > 0) {
          if (!pendingPatchesRef.current) {
            pendingPatchesRef.current = {
              nodesPatches: [], edgesPatches: [],
              nodesInverse: [], edgesInverse: [],
            }
          }
          pendingPatchesRef.current.edgesPatches.push(...patches)
          pendingPatchesRef.current.edgesInverse.unshift(...inversePatches)

          if (debounceRef.current) clearTimeout(debounceRef.current)
          debounceRef.current = setTimeout(flushPatches, DEBOUNCE_MS)
        }

        return nextState
      })
    },
    [rawSetEdges, flushPatches],
  )

  const undo = useCallback(() => {
    // Flush any pending patches first
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    flushPatches()

    if (currentIndexRef.current < 0) return
    const entry = historyRef.current[currentIndexRef.current]
    if (!entry) return

    isUndoRedoRef.current = true
    currentIndexRef.current--

    if (entry.nodesInverse.length > 0) rawSetNodes((prev) => sortParentsFirst(applyPatches(prev, entry.nodesInverse)))
    if (entry.edgesInverse.length > 0) rawSetEdges((prev) => applyPatches(prev, entry.edgesInverse))

    requestAnimationFrame(() => { isUndoRedoRef.current = false })
  }, [rawSetNodes, rawSetEdges, flushPatches])

  const redo = useCallback(() => {
    if (currentIndexRef.current >= historyRef.current.length - 1) return
    currentIndexRef.current++
    const entry = historyRef.current[currentIndexRef.current]
    if (!entry) return

    isUndoRedoRef.current = true

    if (entry.nodesPatches.length > 0) rawSetNodes((prev) => sortParentsFirst(applyPatches(prev, entry.nodesPatches)))
    if (entry.edgesPatches.length > 0) rawSetEdges((prev) => applyPatches(prev, entry.edgesPatches))

    requestAnimationFrame(() => { isUndoRedoRef.current = false })
  }, [rawSetNodes, rawSetEdges])

  const checkpoint = useCallback(() => {
    flushPatches()
  }, [flushPatches])

  return { setNodes, setEdges, undo, redo, checkpoint }
}
